import React, { PureComponent } from 'react';
import EditableTable from "@components/Table/EditableTable";
import { EquipmentChatButton, EquipmentColumns, EquipmentProps } from "@containers/facility/Equipment/EquipmentColumns";
import _ from "lodash";
import actions from "@redux/equipment/actions";
import { connect, useSelector } from "react-redux";
import { api } from "@redux/api";
import { EquipmentFormFields } from "@containers/facility/Equipment/EquipmentFormFields";
import { EquipmentComment } from "@containers/facility/Equipment/EquipmentComment";
import { Button, Drawer, Form, Input, Progress, Tabs, Tooltip, message } from "antd";
import { CommentOutlined, DeleteOutlined } from "@ant-design/icons";
import FunctionalTesting from "@containers/facility/Equipment/FunctionalTesting/FunctionalTesting";
import moment from "moment";
import queryString from "query-string";
import { withRouter } from "@/router/withRouter";
import PermissionControl from "../../../components/Permission/Permission";
import RelatedWorkorder from "@containers/facility/Equipment/RelatedWorkorder/RelatedWorkorder";
import { useAuth, useAuthEditable, useAuthFullAccess } from '@/hooks/use-auth';
import { EventName, useEventBus } from '@/hooks/useEventBus';
import { useSmartNavigate } from '@/hooks/use-smart-navigate';
import { buildFacilityPath, getFacilityContext } from '@/utils/route-context';
import Select from "@components/ResponsiveSelect";
import DatePicker from "@components/ResponsiveDatePicker";
import useEquipmentDeleteFlow from "@containers/facility/Equipment/useEquipmentDeleteFlow";

const { TabPane } = Tabs;

const { Option } = Select;

const normalizeMultiValue = (value) => {
    if (Array.isArray(value)) return value.filter((item) => item != null && item !== "");
    if (typeof value === "string") return value.split(",").filter(Boolean);
    if (value == null || value === "") return [];
    return [value];
};

const appendQueryParam = (parts, key, value) => {
    if (Array.isArray(value)) {
        value
            .filter((item) => item != null && item !== "")
            .forEach((item) => appendQueryParam(parts, key, item));
        return;
    }
    if (value == null || value === "") return;
    parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
};

const EQUIPMENT_MOBILE_EXCLUDE_KEYS = [
    "name",
    "id",
    "category",
    "condition",
    "equipment_score",
    "outstanding_workers",
    "modified_date",
    "chat",
    "created_date",
    "modified_date_hidden",
];

const EQUIPMENT_MOBILE_INCLUDE_KEYS = [
    "building",
    "service",
    "recent_workorders_count",
    "description",
    "location",
    "install_date",
    "equipment_age_score",
    "visual_inspection_score",
    "deficiency_score",
    "last_condition_update",
    "recent_functionform",
    "createdby_name",
    "lastModified_name",
];

const formatEquipmentMobileDate = (value) => {
    if (value == null || value === "") return "-";
    const date = moment(value);
    return date.isValid() ? date.format("YYYY-MM-DD") : "-";
};

const formatEquipmentMobileScore = (value) => {
    if (value == null || value === "") return "-";
    const score = Number(value);
    return Number.isFinite(score) ? score.toFixed(1) : "-";
};

const formatEquipmentMobileCondition = (value) => {
    if (value == null || value === "") return "Null";
    return value;
};

const formatEquipmentMobileOutstandingWO = (value) => {
    if (value == null || value === "") return "-";
    return value;
};

const formatEquipmentMobileOutstandingWOPercent = (value) => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return 0;
    return Math.max(0, Math.min(100, parsed * 10));
};

const EQUIPMENT_CONDITION_THEME = {
    Excellent: "#15803d",
    Good: "#16a34a",
    Fair: "#c2410c",
    Poor: "#b45309",
    Failed: "#b91c1c",
    Null: "#475569",
};

const renderEquipmentMobileHeader = (record, navigateToEquipmentDetail) => {
    const equipmentScore = formatEquipmentMobileScore(record.equipment_score);
    const equipmentCondition = formatEquipmentMobileCondition(record.condition);
    const equipmentOutstandingWO = formatEquipmentMobileOutstandingWO(record.outstanding_workers);
    const equipmentOutstandingWOPercent = formatEquipmentMobileOutstandingWOPercent(record.outstanding_workers);
    const equipmentCategory = record.category || "-";
    const handleTitleClick = (event) => {
        event.stopPropagation();
        navigateToEquipmentDetail(record.id);
    };

    return (
    <div className="equipment-mobile-card">
        <div className="equipment-mobile-card-main">
            <span
                className="equipment-mobile-card-title equipment-mobile-card-title-link"
                title={record.name || ""}
                onClick={handleTitleClick}
            >
                {record.name || "-"}
            </span>
            <span className="equipment-mobile-card-metric equipment-mobile-card-metric-score" title={`Equipment Score: ${equipmentScore}`}>
                <span className="equipment-mobile-card-metric-label">Score:</span>
                <span className="equipment-mobile-card-metric-value">{equipmentScore}</span>
            </span>

            <span className="equipment-mobile-card-actions">
                <span className="equipment-mobile-card-date">
                    {formatEquipmentMobileDate(record.modified_date)}
                </span>
                <span
                    className="equipment-mobile-card-chat"
                    onClick={(event) => event.stopPropagation()}
                >
                    <EquipmentChatButton record={record} />
                </span>
            </span>
        </div>

        <div className="equipment-mobile-card-details">
            <span className="equipment-mobile-card-category" title={`Category: ${equipmentCategory}`}>
                {equipmentCategory}
            </span>

            <div className="equipment-mobile-card-middle">
                <span className="equipment-mobile-card-metric equipment-mobile-card-metric-condition" title={`Condition: ${equipmentCondition}`}>
                    <span className="equipment-mobile-card-metric-label">Cond:</span>
                    <span
                        className="equipment-mobile-card-metric-value"
                        style={{ color: EQUIPMENT_CONDITION_THEME[equipmentCondition] || "#334155" }}
                    >
                        {equipmentCondition}
                    </span>
                </span>
            </div>

            <span className="equipment-mobile-card-metric equipment-mobile-card-metric-wo" title={`Outstanding WO: ${equipmentOutstandingWO}`}>
                <span className="equipment-mobile-card-metric-label">WO:</span>
                <Progress
                    className="equipment-mobile-card-wo-progress"
                    percent={equipmentOutstandingWOPercent}
                    steps={5}
                    showInfo={false}
                    strokeColor={["#f59e0b", "#f59e0b", "#f59e0b", "#f59e0b", "#f59e0b"]}
                />
            </span>
        </div>
    </div>
    );
};

class Equipment extends PureComponent {
    constructor(props) {
        super(props);
        this.tableRef = React.createRef();
        this.filterFormRef = React.createRef();
        this.state = {
            data: [],
            name: '',
            description: '',
            condition: [],
            building: [],
            location: '',
            category: [],
            service: [],
            Equipment_Model: '',
            Equipment_SN: '',
            isLoading: false,
            query: null,
            selectedRecord: null,
            install_date: [null, null],
            created_date: [null, null],
            modify_date: [null, null],
            categoryOptions: [],
            categoryLookupLoading: false,
            serviceOptions: [],
            serviceLookupLoading: false,
            buildingOptions: [],
            buildingLookupLoading: false,
            selectedTab: '1',
            eq_id: '',
            commentDrawerOpen: false,
            commentEquipmentId: null,
        }
        this.debouncedLoadCategoryOptions = _.debounce((keyword) => this.loadDistinctOptions('category', keyword), 250);
        this.debouncedLoadServiceOptions = _.debounce((keyword) => this.loadDistinctOptions('service', keyword), 250);
        this.debouncedLoadBuildingOptions = _.debounce((keyword) => this.loadDistinctOptions('building', keyword), 250);
    }

    componentDidMount() {
        const params = queryString.parse(this.props.location.search);
        console.log('params', params)
        if (params?.condition) {
            this.setState({
                condition: normalizeMultiValue(params.condition)
            })
        }
        if (params?.category) {
            this.setState({
                category: normalizeMultiValue(params.category)
            })
        }
        if (params?.service) {
            this.setState({
                service: normalizeMultiValue(params.service)
            })
        }
        if (params?.building) {
            this.setState({
                building: normalizeMultiValue(params.building)
            })
        }
        if (params?.id) {
            this.setState({
                eq_id: params.id
            })
        }
    }

    componentDidUpdate(prevProps) {
        // Listen for refreshKey change to refresh table
        if (prevProps.refreshKey !== this.props.refreshKey && this.props.refreshKey > 0) {
            if (this.tableRef && this.tableRef.current) {
                this.tableRef.current.refresh();
            }
        }
    }

    handleCreateEquipment = (body) => {
        return api.createEquipment(body).then(
            response => {
                // console.log('create', response);
                return response.data
            }
        ).catch(error => (
            error
        ));
    }

    // 删除结果提示：让「设备删了，工单到底删没删」对用户可见。
    // 后端会回传 deleted_workorder_ids（真正删掉的）与 orphaned_workorder_ids
    // （成了无设备工单但被保留的），两者都为空表示本次没有受影响的工单。
    reportDeleteResult = (data) => {
        const deletedWorkorders = data?.deleted_workorder_ids || [];
        const orphanedWorkorders = data?.orphaned_workorder_ids || [];
        if (deletedWorkorders.length) {
            message.success(
                `${deletedWorkorders.length} work order${deletedWorkorders.length > 1 ? "s" : ""} deleted together with the equipment.`
            );
        }
        if (orphanedWorkorders.length) {
            message.info(
                `${orphanedWorkorders.length} work order${orphanedWorkorders.length > 1 ? "s are" : " is"} kept without equipment.`
            );
        }
    }

    handleDeleteEquipment = async (id) => {
        // 删除前先确认「删完会失去全部设备」的工单，由用户决定是否一并删除
        const resolved = await this.props.deleteFlow?.request([id]);
        if (!resolved) {
            // 用户取消：必须抛错，ProTable 只会在 onDelete 成功时移除该行
            const cancelled = new Error("EQUIPMENT_DELETE_CANCELLED");
            cancelled.silent = true;
            throw cancelled;
        }

        try {
            const response = await api.deleteEquipment(id, resolved.workorderIds);
            if (!response) {
                throw new Error("No response from delete equipment API");
            }
            this.reportDeleteResult(response.data);
            return response.data;
        } catch (error) {
            message.error("Failed to delete equipment.");
            throw error;
        }
    }

    handleBulkInsert = (body) => {
        return api.bulkEquipmentInsert(body).then(
            response => {
                return response.data
            }
        ).catch(error => {
            return (
                error
            )
        });
    }

    handleBulkDelete = async (rows) => {
        const list = (rows || []).filter(Boolean);
        if (!list.length) {
            return { cancelled: true };
        }

        const resolved = await this.props.deleteFlow?.request(list.map((row) => row.id));
        if (!resolved) {
            return { cancelled: true };
        }

        try {
            const response = await api.bulkDeleteEquipment(list, resolved.workorderIds);
            if (!response) {
                throw new Error("No response from bulk delete equipment API");
            }
            this.reportDeleteResult(response.data);
            this.refresh(true);
            this.clearBulkSelection();
            return response.data;
        } catch (error) {
            message.error("Failed to delete the selected equipment.");
            return { error: true };
        }
    }

    clearBulkSelection = () => {
        if (typeof this.tableRef.current?.clearSelected === "function") {
            this.tableRef.current.clearSelected();
        }
    }

    // 浮动批量条上的删除按钮：接管 TableContent 内置的 DeleteConfirm，
    // 避免「确认框 + 工单勾选弹窗」叠成两层（H5 下是两层 Drawer）。
    renderBulkDeleteAction = (rows) => {
        if (!this.props.fullAccess) {
            return null;
        }
        return (
            <Tooltip title="Delete selected">
                <Button
                    type="default"
                    danger
                    shape="circle"
                    className="table-bulk-floating-btn"
                    icon={<DeleteOutlined />}
                    aria-label="Delete selected"
                    onClick={() => this.handleBulkDelete(rows)}
                />
            </Tooltip>
        );
    }

    handOnlineSave = (key, row) => {
        return api.editEquipment(key, row).then(
            response => {

                return response
            }
        ).catch(error => (
            error
        ));
    }

    buildEquipmentQuery = (baseQuery = "", extraParams = {}) => {
        const cleanBaseQuery = String(baseQuery || "").replace(/^\?/, "").replace(/^&/, "");
        const parts = cleanBaseQuery ? [cleanBaseQuery] : [];

        Object.entries(extraParams).forEach(([key, value]) => {
            appendQueryParam(parts, key, value);
        });

        return parts.join("&");
    };

    handleFilter = (query) => {


        this.setState({
            isLoading: true,
        });

        let query_new = this.buildEquipmentQuery(query, {
            id: this.state.eq_id,
            name: this.state.name,
            description: this.state.description,
            condition: this.state.condition,
            building: this.state.building,
            category: this.state.category,
            location: this.state.location,
            service: this.state.service,
            Equipment_Model: this.state.Equipment_Model,
            Equipment_SN: this.state.Equipment_SN,
            install_date_start: this.state.install_date[0] ? moment(this.state.install_date[0]).format('YYYY-MM-DD') : null,
            install_date_end: this.state.install_date[1] ? moment(this.state.install_date[1]).format('YYYY-MM-DD') : null,
            created_date_start: this.state.created_date[0] ? moment(this.state.created_date[0]).format('YYYY-MM-DD') : null,
            created_date_end: this.state.created_date[1] ? moment(this.state.created_date[1]).format('YYYY-MM-DD') : null,
            modify_date_start: this.state.modify_date[0] ? moment(this.state.modify_date[0]).format('YYYY-MM-DD') : null,
            modify_date_end: this.state.modify_date[1] ? moment(this.state.modify_date[1]).format('YYYY-MM-DD') : null,
        })

        return api.filterEquipment(query_new).then(
            response => {
                let dataSource = response.data.results
                return {
                    data: dataSource,
                    success: true,
                    total: response.data.count
                }
            }
        ).catch(error => (
            error
        ));
    }

    getEquipment = (data) => {

        this.setState({
            isLoading: false,
            data
        })

        this.props.setEquipmentSuccess(data)
    }

    EquipmentRelated = (relatedId) => {
        return <>{relatedId ?
            <RelatedWorkorder relatedList={true} relatedId={relatedId} parentEquipment={relatedId} relatedObject={'equipment'} /> : ''}</>
    }

    handlePostComment = (e) => {

        return <EquipmentComment id={e} />
    }

    openMobileCommentDrawer = (record) => {
        this.setState({
            commentDrawerOpen: true,
            commentEquipmentId: record?.id || null,
        });
    }

    closeMobileCommentDrawer = () => {
        this.setState({
            commentDrawerOpen: false,
            commentEquipmentId: null,
        });
    }

    renderMobileRowActions = (record) => {
        return [
            <Button
                key="mobile-comments"
                size="small"
                icon={<CommentOutlined />}
                onClick={() => this.openMobileCommentDrawer(record)}
            >
                Comments
            </Button>
        ];
    }


    refresh = (needRequest) => {
        return this.tableRef?.current?.refresh(needRequest);
    };

    onNew = () => {
        this.props.navigate(this.props.createEquipmentPath);
    };

    loadDistinctOptions = (field, keyword = "") => {
        const loadingKey = `${field}LookupLoading`;
        const optionsKey = `${field}Options`;
        this.setState({ [loadingKey]: true });
        const query = this.buildEquipmentQuery("", {
            [`${field}_keyword`]: keyword || null,
            page_size: 50,
        });

        return api
            .filterEquipment(query)
            .then((response) => {
                const seen = new Set();
                const options = (response.data.results || [])
                    .map((equipment) => equipment[field])
                    .filter((value) => value != null && value !== "")
                    .filter((value) => {
                        if (seen.has(value)) return false;
                        seen.add(value);
                        return true;
                    })
                    .map((value) => ({
                        value,
                        label: value,
                    }));
                this.setState({
                    [optionsKey]: options,
                    [loadingKey]: false,
                });
                return options;
            })
            .catch((error) => {
                this.setState({ [loadingKey]: false });
                return error;
            });
    }

    handleSelect = (e, type) => {
        this.setState({
            [type]: e,
            limit: null
        }, function () {
            this.refresh(false);
        })
    }

    handleSearchSelect = (e, type) => {
        this.setState({
            [type]: e,
            limit: null
        }, function () {
            this.refresh(false);
        })
    }

    handleSearch = (e, type) => {
        this.setState({
            [type]: e?.target ? e.target.value : e,
            limit: null
        }, function () {
            this.refresh(false);
        })
    }

    onDateChange = (dates, state) => {
        this.setState({
            [state]: dates ?? []
        }, function () {
            this.refresh(false);
        })

    }

    renderFilter = () => {
        return null
    }

    resetFilters = () => {
        this.filterFormRef.current.resetFields();
        this.setState({
            name: '',
            description: '',
            condition: [],
            building: [],
            location: '',
            category: [],
            service: [],
            Equipment_Model: '',
            Equipment_SN: '',
            install_date: [null, null],
            created_date: [null, null],
            modify_date: [null, null]
        }, function () {
            this.refresh(false)
        })

    }
    renderSearches = () => {

        return (
            <Form
                ref={this.filterFormRef}
                className={'filter-form'}
                initialValues={{
                    condition: normalizeMultiValue(this.state.condition),
                    category: normalizeMultiValue(this.state.category),
                    service: normalizeMultiValue(this.state.service),
                    building: normalizeMultiValue(this.state.building),
                }}
                autoComplete="off"
            >
                <Form.Item label={'Name'} name="name">
                    <Input
                        allowClear
                        style={{ width: "100%" }}
                        placeholder={'name'}
                        value={this.state.name}
                        onChange={(e) => this.handleSearch(e, 'name')}
                    />
                </Form.Item>

                <Form.Item label={'Description'} name="description">
                    <Input
                        allowClear
                        style={{ width: "100%" }}
                        placeholder={'description'}
                        value={this.state.description}
                        onChange={(e) => this.handleSearch(e, 'description')}
                    />
                </Form.Item>

                <Form.Item label={'Location'} name="location">
                    <Input
                        allowClear
                        style={{ width: "100%" }}
                        placeholder={'location'}
                        value={this.state.location}
                        onChange={(e) => this.handleSearch(e, 'location')}
                    />
                </Form.Item>

                <Form.Item label={'Building'} name={'building'}>
                    <Select
                        mode="multiple"
                        allowClear
                        showSearch
                        filterOption={false}
                        options={this.state.buildingOptions}
                        loading={this.state.buildingLookupLoading}
                        className={"min-multi-selector"}
                        style={{ width: "100%" }}
                        placeholder={'building'}
                        value={normalizeMultiValue(this.state.building)}
                        onFocus={() => this.loadDistinctOptions('building', '')}
                        onSearch={(keyword) => this.debouncedLoadBuildingOptions(keyword)}
                        onChange={(e) => this.handleSearchSelect(e, 'building')}
                    />
                </Form.Item>

                <Form.Item label={'Service'} name={'service'}>
                    <Select
                        mode="multiple"
                        allowClear
                        showSearch
                        filterOption={false}
                        options={this.state.serviceOptions}
                        loading={this.state.serviceLookupLoading}
                        className={"min-multi-selector"}
                        style={{ width: "100%" }}
                        placeholder={'service'}
                        value={normalizeMultiValue(this.state.service)}
                        onFocus={() => this.loadDistinctOptions('service', '')}
                        onSearch={(keyword) => this.debouncedLoadServiceOptions(keyword)}
                        onChange={(e) => this.handleSearchSelect(e, 'service')}
                    />
                </Form.Item>

                <Form.Item label={'Category'} name={'category'}>
                    <Select
                        mode="multiple"
                        allowClear
                        showSearch
                        filterOption={false}
                        options={this.state.categoryOptions}
                        loading={this.state.categoryLookupLoading}
                        className={"min-multi-selector"}
                        style={{ width: "100%" }}
                        placeholder={'category'}
                        value={normalizeMultiValue(this.state.category)}
                        onFocus={() => this.loadDistinctOptions('category', '')}
                        onSearch={(keyword) => this.debouncedLoadCategoryOptions(keyword)}
                        onChange={(e) => this.handleSearchSelect(e, 'category')}
                    />
                </Form.Item>

                <Form.Item label={'Equipment Model'} name={'Equipment_Model'}>
                    <Input
                        allowClear
                        style={{ width: "100%" }}
                        placeholder={'Equipment Model'}
                        value={this.state.Equipment_Model}
                        onChange={(e) => this.handleSearch(e, 'Equipment_Model')}
                    />
                </Form.Item>

                <Form.Item label={'Equipment SN'} name={'Equipment_SN'}>
                    <Input
                        allowClear
                        style={{ width: "100%" }}
                        placeholder={'Equipment SN'}
                        value={this.state.Equipment_SN}
                        onChange={(e) => this.handleSearch(e, 'Equipment_SN')}
                    />
                </Form.Item>

                <Form.Item label={'Condition'} name={'condition'}>
                    <Select
                        mode="multiple"
                        allowClear
                        className={'min-multi-selector'}
                        style={{ width: "100%" }}
                        placeholder={'condition'}
                        value={normalizeMultiValue(this.state.condition)}
                        name={'condition'}
                        onChange={(e) => this.handleSearchSelect(e, 'condition')}>
                        <Option value="Null">Null</Option>
                        <Option value="Excellent">Excellent</Option>
                        <Option value="Good">Good</Option>
                        <Option value="Fair">Fair</Option>
                        <Option value="Poor">Poor</Option>
                        <Option value="Failed">Failed</Option>
                    </Select>
                </Form.Item>

                <Form.Item name={'install_date_start'} label={'Install Date'}>
                    <DatePicker.RangePicker
                        name={'install_date_start'}
                        className={'border-right-6'}
                        value={this.state.install_date}
                        onChange={(dates) => this.onDateChange(dates, 'install_date')}
                    />
                </Form.Item>

                <Form.Item name={'created_date_start'} label={'Created Date'}>
                    <DatePicker.RangePicker
                        value={this.state.created_date}
                        onChange={(dates) => this.onDateChange(dates, 'created_date')}
                    />
                </Form.Item>

                <Form.Item name={'modify_date'} label={'Modified Date'}>
                    <DatePicker.RangePicker
                        value={this.state.modify_date}
                        onChange={(dates) => this.onDateChange(dates, 'modify_date')}
                    />
                </Form.Item>

                {/* <Form.Item name={'install_date_end'} label={'Install Date End'}>
                    <DatePicker
                        name={'install_date_end'}
                        className={'border-left-6'}
                        value={this.state.install_date[1]}
                        placeholder={'install end date'}
                        onChange={(dates) => this.onDateChange(dates, 'install_date', 1, '')}
                    />
                </Form.Item> */}

            </Form>
        )
    }

    renderAdditionalForm = (data) => {
        console.log('record: ', data)
        return (
            <>{data && <FunctionalTesting selectedRecord={data} />}</>
        )
    }

    handleSelectedRecord = (record) => {
        this.setState({
            selectedRecord: record
        })
    }

    handleTab = (key) => {
        this.setState({
            selectedTab: key
        })
    }

    render() {
        return (

            <div className={'p-5'}>
                <EditableTable
                    ref={this.tableRef}
                    columns={EquipmentColumns(this.handleSelectedRecord)}
                    data={this.state.data}
                    columnProps={EquipmentProps}
                    formFields={EquipmentFormFields}
                    formRelated={this.EquipmentRelated}
                    getRecord={this.getEquipment}
                    createRecords={this.handleCreateEquipment}
                    deleteRecord={this.handleDeleteEquipment}
                    bulkInsert={this.handleBulkInsert}
                    bulkDelete={this.handleBulkDelete}
                    handOnlineSave={this.handOnlineSave}
                    handleFilter={this.handleFilter}
                    postComment={this.handlePostComment}
                    renderFilter={this.renderFilter}
                    resetFilters={this.resetFilters}
                    relatedList={this.props.relatedList}

                    hide_search={true}
                    isRowClickable={false}
                    // isChat={true}
                    isRelated={true}
                    isAttachments={true}

                    setFilters={this.setFilters}
                    formClassName={'flex-form'}
                    filterDescription={this.state.filterDescription}

                    renderSearches={this.renderSearches}

                    renderAdditionalForm={this.renderAdditionalForm}
                    isAdditionalForm={true}
                    new_text={'Create Equipment'}
                    onNew={this.onNew}
                    hid_new={!this.props.editable}
                    hide_imports={!this.props.editable}
                    canDelete={false}
                    customButton={this.renderBulkDeleteAction}
                    mobileCardHeaderRender={(record) => renderEquipmentMobileHeader(record, this.props.navigateToEquipmentDetail)}
                    mobileRowActions={this.renderMobileRowActions}
                    mobileExpandedExcludeKeys={EQUIPMENT_MOBILE_EXCLUDE_KEYS}
                    mobileExpandedIncludeKeys={EQUIPMENT_MOBILE_INCLUDE_KEYS}
                    mobileInfiniteScroll={true}
                    mobilePageSize={20}
                    defaultDisplayColumns={{
                        "id": {
                            "show": false
                        },
                        "description": {
                            "show": false
                        },
                        "location": {
                            "show": false
                        },
                        "install_date": {
                            "show": false
                        },
                        "equipment_age_score": {
                            "show": false
                        },
                        "visual_inspection_score": {
                            "show": false
                        },
                        "deficiency_score": {
                            "show": false
                        },
                        "last_condition_update": {
                            "show": false
                        },
                        "recent_functionform": {
                            "show": false
                        },
                        "createdby_name": {
                            "show": false
                        },
                        "lastModified_name": {
                            "show": false
                        }
                    }}
                />
                <Drawer
                    title="Comments"
                    placement="bottom"
                    height="85vh"
                    open={this.state.commentDrawerOpen}
                    onClose={this.closeMobileCommentDrawer}
                    destroyOnClose={true}
                    className="equipment-comment-mobile-drawer"
                    bodyStyle={{ padding: 0 }}
                >
                    <EquipmentComment
                        id={this.state.commentEquipmentId}
                        boxHeight="calc(85vh - 56px)"
                    />
                </Drawer>

                {this.props.deleteFlow?.modalNode}

            </div>

        );
    }
}

const mapStateToProps = state => {
    return {
        Equipment: _.get(state.Equipment, 'data'),
    }
}
const mapDispatchToProps = dispatch => {
    return {

        setEquipmentRequest: (e) => dispatch(actions.EquipmentRequest(e)),
        setEquipmentSuccess: (e) => dispatch(actions.EquipmentSuccess(e)),
    }
}

const withHooks = (Component) => {
    const NewComponent = (props) => {
        const editable = useAuthEditable('equipment');
        const fullAccess = useAuthFullAccess('equipment')
        const deleteFlow = useEquipmentDeleteFlow();
        const { navigateToEquipmentDetail } = useSmartNavigate();
        const facilityContext = useSelector((state) => getFacilityContext(state));
        const createEquipmentPath = buildFacilityPath('create-equipment', facilityContext) || '/switch-view';

        const [refreshKey, setRefreshKey] = React.useState(0);

        useEventBus(EventName.REFRESH_EQUIPMENT_LIST, () => {
            setRefreshKey(prev => prev + 1);
        });

        return <Component {...props} editable={editable} fullAccess={fullAccess} refreshKey={refreshKey} createEquipmentPath={createEquipmentPath} navigateToEquipmentDetail={navigateToEquipmentDetail} deleteFlow={deleteFlow} />
    }
    return NewComponent;
}

export default withHooks(withRouter(connect(
    mapStateToProps,
    mapDispatchToProps
)(Equipment)));
